import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Check, X as XIcon, CalendarOff, Trash2, Eye } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';
import { PAGE_WIDE, DataTable, SearchInput, Tabs, OverflowMenu, useUrlTab, num, plural } from '../../components/ui';
import { leaveTypeLabel } from '../Staff/hrKit';
import {
  LeaveStatus, LeaveRequestDialog, LeaveReviewDrawer, useLeaveDecisions, fmtPeriod, leaveDays, inRange, isDeclined,
} from './leaveKit';
import LeaveBalances from './LeaveBalances';

export default function Leave() {
  const { user, isOffice } = useAuth();
  const canReview = isOffice;
  const confirm = useConfirm();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [staff, setStaff] = useState([]);
  const [status, setStatus] = useUrlTab('status', 'pending', ['all', 'pending', 'approved', 'declined', 'balances']);
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [requestOpen, setRequestOpen] = useState(false);
  const [reviewing, setReviewing] = useState(null);

  // Everything is loaded once so the counts on each segment are real, whatever is showing.
  const load = useCallback(() => {
    setLoading(true); setLoadError(null);
    api.get('/leave')
      .then((r) => setRequests(Array.isArray(r.data) ? r.data : []))
      .catch((e) => setLoadError(e.response?.data?.message || "We couldn't load leave requests. Check your connection, then try again."))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (canReview) api.get('/staff').then((r) => setStaff(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, [canReview]);

  const { approve, decline, declineDialog } = useLeaveDecisions(load);

  const counts = useMemo(() => ({
    all: requests.length,
    pending: requests.filter((r) => r.status === 'pending').length,
    approved: requests.filter((r) => r.status === 'approved').length,
    declined: requests.filter((r) => isDeclined(r.status)).length,
  }), [requests]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return requests.filter((r) => {
      if (status === 'pending' && r.status !== 'pending') return false;
      if (status === 'approved' && r.status !== 'approved') return false;
      if (status === 'declined' && !isDeclined(r.status)) return false;
      if (q && !(r.staff_name || '').toLowerCase().includes(q)) return false;
      if ((from || to) && !inRange(r, from, to)) return false;
      return true;
    });
  }, [requests, status, search, from, to]);

  const canDeleteRow = (r) => r.status === 'pending' ? (canReview || r.staff_id === user?.id) : canReview;
  const deleteRequest = async (r) => {
    const ok = await confirm(`${leaveTypeLabel(r.leave_type)} leave for ${r.staff_name}, ${fmtPeriod(r.start_date, r.end_date)}, will be deleted. This can't be undone.`,
      { title: 'Delete leave request?', confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    try {
      await api.delete(`/leave/${r.id}`);
      toast.success('Leave request deleted');
      setReviewing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.message || "The request wasn't deleted. Try again."); }
  };

  const columns = [
    { key: 'staff', header: 'Staff', sortable: true, sortValue: (r) => r.staff_name || '', render: (r) => (
      <Link to={`/staff/${r.staff_id}/leave`} onClick={(e) => e.stopPropagation()} className="whitespace-nowrap font-medium text-slate-900 hover:text-primary-700 hover:underline">{r.staff_name}</Link>
    ) },
    { key: 'type', header: 'Type', sortable: true, sortValue: (r) => leaveTypeLabel(r.leave_type), render: (r) => <span className="whitespace-nowrap">{leaveTypeLabel(r.leave_type)}</span> },
    { key: 'period', header: 'Period', sortable: true, sortValue: (r) => String(r.start_date || '').slice(0, 10), render: (r) => <span className="whitespace-nowrap">{fmtPeriod(r.start_date, r.end_date)}</span> },
    { key: 'days', header: 'Days', align: 'right', sortable: true, sortValue: (r) => leaveDays(r.start_date, r.end_date) || 0, render: (r) => <span className="tabular-nums">{num(leaveDays(r.start_date, r.end_date) || 0)}</span> },
    { key: 'status', header: 'Status', render: (r) => <LeaveStatus status={r.status} /> },
    { key: 'impact', header: 'Roster', render: (r) => (r.status === 'pending'
      ? (Number(r.conflict_count) > 0 ? <span className="badge-yellow whitespace-nowrap" title="Approving unassigns these shifts">{plural(r.conflict_count, 'shift')}</span> : <span className="text-[12.5px] text-slate-500">No shifts</span>)
      : <span className="text-slate-400">—</span>) },
    { key: 'notes', header: 'Notes', className: 'max-w-[200px]', render: (r) => (r.notes ? <span className="block truncate text-slate-600" title={r.notes}>{r.notes}</span> : <span className="text-slate-400">—</span>) },
    { key: 'actions', header: <span className="sr-only">Actions</span>, align: 'right', render: (r) => (
      <span className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
        {canReview && r.status === 'pending' && (
          <>
            <button type="button" onClick={() => approve(r)} className="btn-primary btn-sm" aria-label={`Approve leave for ${r.staff_name}`}><Check size={13} aria-hidden="true" />Approve</button>
            <button type="button" onClick={() => decline(r)} className="btn-secondary btn-sm" aria-label={`Decline leave for ${r.staff_name}`}><XIcon size={13} aria-hidden="true" />Decline</button>
          </>
        )}
        <OverflowMenu label={`More actions for ${r.staff_name}'s leave`} items={[
          { label: 'Review details', icon: Eye, onClick: () => setReviewing(r) },
          canDeleteRow(r) && { divider: true },
          canDeleteRow(r) && { label: 'Delete request', icon: Trash2, danger: true, onClick: () => deleteRequest(r) },
        ].filter(Boolean)} />
      </span>
    ) },
  ];

  const tabs = [
    { id: 'pending', label: 'Pending', count: counts.pending },
    { id: 'approved', label: 'Approved', count: counts.approved },
    { id: 'declined', label: 'Declined', count: counts.declined },
    { id: 'all', label: 'All', count: counts.all },
    canReview && { id: 'balances', label: 'Balances' },
  ].filter(Boolean);
  const filtersOn = !!(search || from || to);
  // Entitlements per worker (admin/office), in place of the request list.
  const showBalances = canReview && status === 'balances';
  const tabsNode = <Tabs variant="segmented" size="sm" ariaLabel="Leave status" tabs={tabs} value={status} onChange={setStatus} className="self-start" />;

  return (
    <div className={PAGE_WIDE}>
      {showBalances ? (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="border-b border-slate-200 px-4 py-3">{tabsNode}</div>
          <LeaveBalances staff={staff} />
        </div>
      ) : (
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-3 lg:flex-row lg:flex-wrap lg:items-center">
          {tabsNode}
          <SearchInput value={search} onChange={setSearch} placeholder="Search staff" className="w-full lg:w-56" />
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[12.5px] font-medium text-slate-600">Dates</span>
            <input type="date" aria-label="Leave from" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-[13px] text-slate-700" />
            <span className="text-slate-400" aria-hidden="true">–</span>
            <input type="date" aria-label="Leave to" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-[13px] text-slate-700" />
            {(from || to) && <button type="button" onClick={() => { setFrom(''); setTo(''); }} className="text-[12px] font-medium text-slate-500 hover:text-slate-800 hover:underline">Clear dates</button>}
          </div>
          <div className="flex items-center gap-3 lg:ml-auto">
            {!loading && counts.pending > 0 && status !== 'pending' && <span className="text-[12.5px] text-slate-600">{plural(counts.pending, 'pending request')}</span>}
            <button type="button" onClick={() => setRequestOpen(true)} className="btn-primary btn-sm"><Plus size={14} aria-hidden="true" />Request leave</button>
          </div>
        </div>
        <DataTable
          label="Leave requests"
          columns={columns}
          rows={filtered}
          loading={loading}
          error={loadError}
          onRetry={load}
          defaultSort={{ key: 'period', dir: 'asc' }}
          onRowClick={(r) => setReviewing(r)}
          empty={requests.length === 0
            ? { icon: CalendarOff, title: 'No leave requests yet', hint: 'Requests from staff land here for approval. You can also request leave on their behalf.', action: <button type="button" onClick={() => setRequestOpen(true)} className="btn-primary btn-sm"><Plus size={14} aria-hidden="true" />Request leave</button> }
            : status === 'pending' && !filtersOn
              ? { icon: CalendarOff, title: 'Nothing waiting for approval', hint: 'New requests will appear here.', action: <button type="button" onClick={() => setStatus('all')} className="btn-secondary btn-sm">Show all requests</button> }
              : { icon: CalendarOff, title: 'No requests match', hint: 'Try another status, name or date range.', action: <button type="button" onClick={() => { setSearch(''); setFrom(''); setTo(''); setStatus('all'); }} className="btn-secondary btn-sm">Clear filters</button> }}
        />
        {!loading && !loadError && filtered.length > 0 && (
          <p className="border-t border-slate-100 px-4 py-2 text-[12px] text-slate-500">{plural(filtered.length, 'request')}{filtered.length !== requests.length ? ` of ${num(requests.length)}` : ''}</p>
        )}
      </div>
      )}

      <LeaveRequestDialog open={requestOpen} onClose={() => setRequestOpen(false)} onSaved={load} staff={staff} canPickStaff={canReview} />
      <LeaveReviewDrawer
        request={reviewing}
        allRequests={requests}
        canReview={canReview}
        canDelete={reviewing ? canDeleteRow(reviewing) : false}
        onApprove={async (r, hours) => { if (await approve(r, hours)) setReviewing(null); }}
        onDecline={(r) => { setReviewing(null); decline(r); }}
        onDelete={deleteRequest}
        onClose={() => setReviewing(null)}
      />
      {declineDialog}
    </div>
  );
}
